import { sfParameters } from '../sf-names';
import { DataItem, ValidationItemParameter, ValidationItem, ValidationWire } from './types';

const dataItemToValidation = (data: DataItem): ValidationItemParameter => {
  return data && Object.keys(data).reduce(
    (store, key) => {
      return {
        ...store,
        [key]: data[key].v
      };
    },
    {} as ValidationItemParameter);
};

const compareParam = (dataObject: DataItem, validationObject: ValidationItemParameter, param: string) =>
  !!dataObject[param] && dataObject[param].v === validationObject[param];

const copyParametersForApplication = (params: ValidationItemParameter, data: DataItem[]) => {

  const keys = Object.keys(params);

  return keys.length === 1 && (keys[0] === sfParameters.application || keys[0] === sfParameters.caseType)
    ? data
      .filter(dataItem => dataItem &&
        compareParam(dataItem, params, sfParameters.application) ||
        compareParam(dataItem, params, sfParameters.caseType))
      .map(dataItemToValidation)
    : undefined;
};

const copyParametersForDefaultValueCodetable = (params: ValidationItemParameter, data: DataItem[]) => {

  const keys = Object.keys(params);

  return keys.length === 2 &&
    keys.some(key => key === sfParameters.caseType) &&
    keys.some(key => key === sfParameters.codeTable)
    ? data
      .filter(dataItem => dataItem &&
        (compareParam(dataItem, params, sfParameters.caseType) && compareParam(dataItem, params, sfParameters.codeTable)))
      .map(dataItemToValidation)
    : undefined;
};

const copyParametersForDefaultValueField = (params: ValidationItemParameter, data: DataItem[]) => {

  const keys = Object.keys(params);

  return keys.length === 2 &&
    keys.some(key => key === sfParameters.field) &&
    keys.some(key => key === sfParameters.caseType || key === sfParameters.codeTable)
    ? data
      .filter(dataItem => dataItem &&
        compareParam(dataItem, params, sfParameters.field) &&
        (compareParam(dataItem, params, sfParameters.caseType) || compareParam(dataItem, params, sfParameters.codeTable)))
      .map(dataItemToValidation)
    : undefined;
};

export const normalizeValidationParameters = (validationItem: ValidationItem, data: DataItem[]): ValidationItem[] => {

  if (!data || !validationItem || !validationItem.parameters) {
    return validationItem ? [validationItem] : [];
  }

  const params = validationItem.parameters;
  const validationParameters =
    copyParametersForApplication(params, data) ||
    copyParametersForDefaultValueCodetable(params, data) ||
    copyParametersForDefaultValueField(params, data) ||
    [params];

  return validationParameters.map(parameters => ({
    message: validationItem.message,
    ...(parameters !== undefined && { parameters })
  }));
};

export const normalizeValidationItems = (validationItems: ValidationItem[], data: DataItem[]): ValidationItem[] => {

  return validationItems && validationItems.reduce(
    (store, validationItem) => {

      return [
        ...store,
        ...normalizeValidationParameters(validationItem, data)
          .filter(item => {

            const itemKeys = Object.keys(item && item.parameters || {});

            return !store.some(storeItem => {

              const storeItemKeys = Object.keys(storeItem && storeItem.parameters || {});

              return item.message === storeItem.message &&
                itemKeys.length === storeItemKeys.length &&
                storeItemKeys.every(
                  key => item.parameters[key] === storeItem.parameters[key]
                );
            });
          })
      ];
    },
    [] as ValidationItem[]);
};

export const normalizeValidation = <T>(validationWire: ValidationWire<T>, data: T) => {

  return data && validationWire && Object.keys(validationWire).reduce(
    (store, key) => {

      const validationItem: ValidationItem[] = validationWire[key];
      const normalizedItem = normalizeValidationItems(validationItem, data[key]);

      return {
        ...store,
        [key]: normalizedItem
      };
    }, {} as ValidationWire<T>) || validationWire;
};
